// Translation strings for the payroll-integrations microapp.
//
// Same hand-rolled pattern as workflow-hub-v2 and onboarding-form: one flat key
// map per language, with {var} placeholders resolved by useTranslation's `vars`
// argument. No i18n library — the app is a Next.js static export and the repo
// keeps this deliberately dependency-free.
//
// To add a string:
//   1. Add the key to TranslationKey
//   2. Add it to BOTH the `en` and `fr` blocks below (TS fails the build otherwise)
//   3. Replace the literal in the component with t('your.key') — or
//      t('your.key', { name }) for interpolation
//
// Keys are flat and grouped by area with a prefix (common. / page. / workers. /
// detail. / editDetails. / backfill. / mappings. / providers. / credentials. /
// field. / pager.). Prefer reusing an existing key over adding a second key
// with identical text.
//
// NOT translated on purpose: anything the server produces at runtime (API error
// text, gate reasons, provider names, worker names, raw JSON bodies) and the
// value-translation data itself.

import type { Language } from "@/lib/locale"

export type TranslationKey =
  // Common actions, chrome and shared nouns
  | "common.skipToMain"
  | "common.save"
  | "common.cancel"
  | "common.close"
  | "common.done"
  | "common.tryAgain"
  | "common.open"
  | "common.loading"
  | "common.refresh"
  | "common.filters"
  | "common.activeFilters"
  | "common.clearFilters"
  | "common.clearAll"
  | "common.removeFilterAria"
  | "common.sortPrefix"
  | "common.sortByAria"
  | "common.sortedAscending"
  | "common.sortedDescending"
  | "common.all"
  | "common.any"
  | "common.status"
  | "common.from"
  | "common.upTo"
  | "common.on"
  | "common.off"
  | "common.notSet"
  | "common.added"
  | "common.payrollSystem"
  | "common.chooseOrganization"
  | "common.notInCurrentListValue"
  | "common.env.sandbox"
  | "common.env.production"
  | "common.env.live"

  // Table pager
  | "pager.showing"
  | "pager.perPage"
  | "pager.rowsPerPageAria"
  | "pager.previousPage"
  | "pager.nextPage"
  | "pager.pageOf"

  // Page shell and tabs
  | "page.title"
  | "page.subtitle"
  | "tabs.workers"
  | "tabs.providers"
  | "tabs.mappings"
  | "tabs.backfill"
  | "hub.tablistAria"
  | "hub.workers.sub"
  | "hub.providers.noneYet"
  | "hub.mappings.sub"
  | "hub.backfill.sub"
  | "notFound.message"

  // Workers (migrations) tab
  | "workers.intro"
  | "workers.error.load"
  | "workers.toast.detailLoadFailed"
  | "workers.card.all"
  | "workers.card.needsAttention"
  | "workers.card.addedToPayroll"
  | "workers.searchPlaceholder"
  | "workers.limitNotice"
  | "workers.chip.addedFrom"
  | "workers.chip.addedTo"
  | "workers.filter.progress"
  | "workers.filter.addedBetween"
  | "workers.filter.byPayrollSystemAria"
  | "workers.filter.byProgressAria"
  | "workers.countOf"
  | "workers.empty"
  | "workers.addedOn"
  | "workers.profileNumber"
  | "workers.meta.profile"
  | "workers.meta.employeeId"
  | "workers.meta.sentTo"
  | "workers.meta.tries"
  | "workers.meta.whatHappened"
  | "workers.sort.worker"
  | "workers.sort.organization"
  | "workers.status.pending"
  | "workers.status.waitingForWorkOrder"
  | "workers.status.sent"
  | "workers.status.created"
  | "workers.status.failed"
  | "workers.status.skipped"
  | "workers.status.ignored"
  | "workers.status.resolved"

  // Worker detail drawer
  | "detail.goingToLive"
  | "detail.goingToEnv"
  | "detail.meta.fboProfile"
  | "detail.meta.employeeIdAt"
  | "detail.meta.triesSoFar"
  | "detail.meta.accepted"
  | "detail.meta.lastActionBy"
  | "detail.meta.handledBy"
  | "detail.acceptedOn"
  | "detail.acceptedNoDate"
  | "detail.whatWentWrong"
  | "detail.whyWaiting"
  | "detail.gateHint"
  | "detail.advanced"
  | "detail.sentHeading"
  | "detail.wouldSendHeading"
  | "detail.noStoredBodySent"
  | "detail.noStoredBodyCreated"
  | "detail.workingOutPreview"
  | "detail.previewWouldSendNow"
  | "detail.previewNothingSentYet"
  | "detail.noSentBodyRecorded"
  | "detail.nothingSentYet"
  | "detail.previewFailed"
  | "detail.answerHeading"
  | "detail.reference"
  | "detail.sentOn"
  | "detail.action.sendNow"
  | "detail.action.checkWorkOrder"
  | "detail.action.editDetails"
  | "detail.action.markHandled"
  | "detail.action.markHandledHint"
  | "detail.action.dontSend"
  | "detail.action.dontSendHint"
  | "detail.toast.sending"
  | "detail.toast.checkingWorkOrder"
  | "detail.toast.tryingAgain"
  | "detail.toast.markedHandled"
  | "detail.toast.willNotSend"
  | "detail.toast.actionFailed"

  // Edit details drawer
  | "editDetails.title"
  | "editDetails.fallbackWorker"
  | "editDetails.description"
  | "editDetails.noPayload.typeNotEnabled"
  | "editDetails.noPayload.multipleOrgs"
  | "editDetails.noPayload.noProvider"
  | "editDetails.noPayload.waitingForWorkOrder"
  | "editDetails.noPayload.generic"
  | "editDetails.group.whoTheyAre"
  | "editDetails.group.howToReach"
  | "editDetails.group.theirWork"
  | "editDetails.group.address"
  | "editDetails.group.orgCodes"
  | "editDetails.hint.providerEmployeeId"
  | "editDetails.hint.lastName"
  | "editDetails.hint.middleName"
  | "editDetails.hint.middleNameSplit"
  | "editDetails.hint.hireDate"
  | "editDetails.hint.payType"
  | "editDetails.hint.workLocation"
  | "editDetails.hint.ein"
  | "editDetails.maxLengthHint"
  | "editDetails.internalOrganization"
  | "editDetails.orgHint"
  | "editDetails.carriedThrough"
  | "editDetails.saveAndSend"
  | "editDetails.saveForLater"
  | "editDetails.toast.savedAndSending"
  | "editDetails.toast.saved"
  | "editDetails.toast.savedDescription"
  | "editDetails.toast.sendFailed"
  | "editDetails.toast.saveFailed"

  // Outgoing-payload field labels (shared by the edit form and the providers tab)
  | "field.userProfileId"
  | "field.providerEmployeeId"
  | "field.category"
  | "field.firstName"
  | "field.lastName"
  | "field.middleName"
  | "field.email"
  | "field.profileTypeValue"
  | "field.profileTypeCode"
  | "field.hireDate"
  | "field.payType"
  | "field.hourlyRate"
  | "field.normalHours"
  | "field.department"
  | "field.workersCompCode"
  | "field.workLocation"
  | "field.ein"
  | "field.internalOrganizationId"
  | "field.internalOrganizationCode"
  | "field.providerClientId"
  | "field.legalCode"
  | "field.payGroup"
  | "field.jobTitle"
  | "field.address1"
  | "field.address2"
  | "field.city"
  | "field.provinceOrState"
  | "field.postalOrZip"
  | "field.countryCode"
  | "field.homePhone"
  | "field.mobilePhone"
  | "field.additionalFields"
  | "field.organizationFields"

  // Add existing workers (backfill) tab
  | "backfill.intro"
  | "backfill.yourPayrollSystem"
  | "backfill.error.load"
  | "backfill.searchPlaceholder"
  | "backfill.limitNotice"
  | "backfill.filter.anyStatus"
  | "backfill.filter.allTypes"
  | "backfill.filter.byWorkerTypeAria"
  | "backfill.filter.byStatusAria"
  | "backfill.status.ready"
  | "backfill.status.typeNotSent"
  | "backfill.status.typeNotSentLong"
  | "backfill.status.typeNotSentHint"
  | "backfill.selectAllReady"
  | "backfill.selectWorkerAria"
  | "backfill.addSelected"
  | "backfill.addSelectedOne"
  | "backfill.addSelectedCount"
  | "backfill.toast.queuedOne"
  | "backfill.toast.queued"
  | "backfill.toast.queuedDescription"
  | "backfill.toast.addFailed"

  // Value translations (mappings) tab
  | "mappings.intro"
  | "mappings.error.load"
  | "mappings.hint.department"
  | "mappings.hint.workersCompCode"
  | "mappings.hint.workLocation"
  | "mappings.header.fboValue"
  | "mappings.header.providerValue"
  | "mappings.header.note"
  | "mappings.aria.fboValue"
  | "mappings.aria.providerValue"
  | "mappings.aria.note"
  | "mappings.aria.removeLine"
  | "mappings.placeholder.chooseValue"
  | "mappings.placeholder.fboValue"
  | "mappings.placeholder.providerValue"
  | "mappings.placeholder.note"
  | "mappings.addLine"
  | "mappings.notListedNote"
  | "mappings.toast.incompleteLine"
  | "mappings.toast.duplicateValue"
  | "mappings.toast.duplicateValueDescription"
  | "mappings.toast.saved"
  | "mappings.toast.saveFailed"

  // Payroll systems (providers) tab
  | "providers.oneSetPerProvider"
  | "providers.sending"
  | "providers.notSetUp"
  | "providers.error.load"
  | "providers.toast.settingsSaved"
  | "providers.headerDescription"
  | "providers.testConnection"
  | "providers.testSuccess"
  | "providers.toast.chooseEnvironment"
  | "providers.toast.saveFailed"
  | "providers.sendingLabel"
  | "providers.sendTo"
  | "providers.baseUrl"
  | "providers.baseUrlHint"
  | "providers.clientIdLabel"
  | "providers.clientIdHint"
  | "providers.clientIdPlaceholder"
  | "providers.paymentFormat.title"
  | "providers.paymentFormat.hint"
  | "providers.paymentFormat.available"
  | "providers.paymentFormat.default"
  | "providers.orgs.title"
  | "providers.orgs.add"
  | "providers.orgs.hint"
  | "providers.orgs.empty"
  | "providers.orgs.header.fboId"
  | "providers.orgs.header.orgCode"
  | "providers.orgs.header.orgName"
  | "providers.orgs.header.clientId"
  | "providers.orgs.aria.fboId"
  | "providers.orgs.aria.clientIdOverride"
  | "providers.orgs.aria.remove"
  | "providers.orgs.placeholder.fboId"
  | "providers.orgs.placeholder.orgCode"
  | "providers.orgs.placeholder.usesDefault"
  | "providers.orgs.placeholder.legalCode"
  | "providers.orgs.dontSend"
  | "providers.types.title"
  | "providers.types.hint"
  | "providers.types.notInList"
  | "providers.how.title"
  | "providers.how.perOrg"
  | "providers.how.c2cLastName"
  | "providers.how.hint"
  | "providers.extras.title"
  | "providers.extras.hint"
  | "providers.standard.hint"
  | "providers.standard.aria.fieldName"
  | "providers.standard.aria.fieldValue"
  | "providers.standard.placeholder.name"
  | "providers.standard.placeholder.value"
  | "providers.standard.removeValue"
  | "providers.standard.addValue"
  | "providers.standard.addOrgAssignment"
  | "providers.standard.orgAssignmentsHint"
  | "providers.standard.aria.assignmentTitle"
  | "providers.standard.aria.assignmentValue"
  | "providers.standard.aria.assignmentNote"
  | "providers.standard.removeAssignment"
  | "providers.standard.placeholder.assignmentTitle"
  | "providers.standard.placeholder.assignmentValue"
  | "providers.audience.all.label"
  | "providers.audience.all.hint"
  | "providers.audience.individual.label"
  | "providers.audience.individual.hint"
  | "providers.audience.organization.label"
  | "providers.audience.organization.hint"
  | "providers.triggers.title"
  | "providers.triggers.onApproval"
  | "providers.triggers.onChange"
  | "providers.triggers.onTermination"
  | "providers.triggers.reconcile"
  | "providers.triggers.hint"

  // Sign-in details (credentials)
  | "credentials.title"
  | "credentials.hint"
  | "credentials.lastChanged"
  | "credentials.lastChangedBy"
  | "credentials.environmentLabel"
  | "credentials.clientId"
  | "credentials.clientSecret"
  | "credentials.placeholderSaved"
  | "credentials.placeholderUnset"
  | "credentials.save"
  | "credentials.toast.nothingEntered"
  | "credentials.toast.saved"
  | "credentials.toast.savedDescription"
  | "credentials.toast.saveFailed"

export const translations: Record<Language, Record<TranslationKey, string>> = {
  en: {
    // Common
    "common.skipToMain": "Skip to main content",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.close": "Close",
    "common.done": "Done",
    "common.tryAgain": "Try again",
    "common.open": "Open",
    "common.loading": "Loading…",
    "common.refresh": "Refresh",
    "common.filters": "Filters",
    "common.activeFilters": "Active",
    "common.clearFilters": "Clear filters",
    "common.clearAll": "Clear all",
    "common.removeFilterAria": "Remove filter: {label}",
    "common.sortPrefix": "Sort:",
    "common.sortByAria": "Sort by",
    "common.sortedAscending": "Sorted ascending — switch to descending",
    "common.sortedDescending": "Sorted descending — switch to ascending",
    "common.all": "All",
    "common.any": "Any",
    "common.status": "Status",
    "common.from": "From",
    "common.upTo": "Up to",
    "common.on": "On",
    "common.off": "Off",
    "common.notSet": "Not set",
    "common.added": "Added",
    "common.payrollSystem": "Payroll system",
    "common.chooseOrganization": "Choose an organization",
    "common.notInCurrentListValue": "{value} (not in the current list)",
    "common.env.sandbox": "Test system (sandbox)",
    "common.env.production": "Live system (production)",
    "common.env.live": "live",

    // Pager
    "pager.showing": "Showing {first}–{last} of {total}",
    "pager.perPage": "Per page",
    "pager.rowsPerPageAria": "Rows per page",
    "pager.previousPage": "Previous page",
    "pager.nextPage": "Next page",
    "pager.pageOf": "Page {page} of {pageCount}",

    // Page shell
    "page.title": "Payroll Integrations",
    // Sits beside the title in the header band, so it stays to one line.
    "page.subtitle": "Workers, payroll systems, translations and backfill in one place",
    "tabs.workers": "Workers (migrations)",
    "tabs.providers": "Payroll systems (providers)",
    "tabs.mappings": "Value translations (mappings)",
    "tabs.backfill": "Add existing workers (backfill)",
    // Captions beneath the figure on each hub card.
    "hub.tablistAria": "Payroll integrations sections",
    "hub.workers.sub": "workers tracked",
    "hub.providers.noneYet": "nothing is being sent yet",
    "hub.mappings.sub": "translation lists in use",
    "hub.backfill.sub": "not added yet",
    "notFound.message": "Page not found.",

    // Workers
    "workers.intro":
      "Every worker being sent to your payroll system, and where each one stands. Open a worker to see or fix their details.",
    "workers.error.load":
      "The list could not be loaded. Check that the Payroll Integrations service is reachable.",
    "workers.toast.detailLoadFailed": "The worker's details could not be loaded",
    "workers.card.all": "All workers",
    "workers.card.needsAttention": "Needs attention",
    "workers.card.addedToPayroll": "Added to payroll",
    "workers.searchPlaceholder": "Search by worker name",
    "workers.limitNotice":
      "Only the newest {count} matching workers are shown — narrow the search, the progress filter, or the date range to see the rest.",
    "workers.chip.addedFrom": "Added from {date}",
    "workers.chip.addedTo": "Added up to {date}",
    "workers.filter.progress": "Progress",
    "workers.filter.addedBetween": "Added between",
    "workers.filter.byPayrollSystemAria": "Filter by payroll system",
    "workers.filter.byProgressAria": "Filter by progress",
    "workers.countOf": "{shown} of {total} workers",
    "workers.empty": "No workers match the current filters.",
    "workers.addedOn": "Added {date}",
    "workers.profileNumber": "Profile {id}",
    "workers.meta.profile": "Profile",
    "workers.meta.employeeId": "Employee ID",
    "workers.meta.sentTo": "Sent to",
    "workers.meta.tries": "Tries",
    "workers.meta.whatHappened": "What happened",
    "workers.sort.worker": "Worker",
    "workers.sort.organization": "Organization",
    "workers.status.pending": "Getting ready (Pending)",
    "workers.status.waitingForWorkOrder": "Waiting for a work order",
    "workers.status.sent": "Sending (Sent)",
    "workers.status.created": "Added to payroll (Created)",
    "workers.status.failed": "Needs attention (Failed)",
    "workers.status.skipped": "Skipped",
    "workers.status.ignored": "Dismissed (Ignored)",
    "workers.status.resolved": "Handled manually (Resolved)",

    // Worker detail
    "detail.goingToLive": "Going to {provider} (live)",
    "detail.goingToEnv": "Going to {provider} ({environment})",
    "detail.meta.fboProfile": "FBO profile",
    "detail.meta.employeeIdAt": "Employee ID at {provider}",
    "detail.meta.triesSoFar": "Tries so far",
    "detail.meta.accepted": "Accepted",
    "detail.meta.lastActionBy": "Last action by",
    "detail.meta.handledBy": "Handled by",
    "detail.acceptedOn": "{provider} accepted this worker on {date} — nothing more to do here.",
    "detail.acceptedNoDate": "{provider} accepted this worker — nothing more to do here.",
    "detail.whatWentWrong": "What went wrong",
    "detail.whyWaiting": "Why this worker is waiting",
    "detail.gateHint":
      "It will go automatically once the work order is in place — or use \"Edit details\" to fill in what's missing and \"Send now\" to send it anyway.",
    "detail.advanced": "Advanced: the exact data exchanged",
    "detail.sentHeading": "What FBO sent to {provider}",
    "detail.wouldSendHeading": "What FBO would send to {provider}",
    "detail.noStoredBodySent":
      "A create was issued to {provider} but no sent body was recorded — this record predates FBO keeping them.",
    "detail.noStoredBodyCreated":
      "No sent body was recorded for this worker — either it predates FBO keeping them, or the worker already existed at {provider} and no create was sent.",
    "detail.workingOutPreview": "Working out what would be sent…",
    "detail.previewWouldSendNow": "This is what the same worker would send now.",
    "detail.previewNothingSentYet": "Nothing has been sent yet — this is what would go.",
    "detail.noSentBodyRecorded": "No sent body was recorded for this worker.",
    "detail.nothingSentYet": "Nothing has been sent yet.",
    "detail.previewFailed": "Could not work out what would be sent.",
    "detail.answerHeading": "What {provider} answered",
    "detail.reference": "Reference {reference}",
    "detail.sentOn": "Sent {date}",
    "detail.action.sendNow": "Send now",
    "detail.action.checkWorkOrder": "Check the work order again",
    "detail.action.editDetails": "Edit details",
    "detail.action.markHandled": "Mark as handled",
    "detail.action.markHandledHint":
      "Use this when you have taken care of the worker outside FBO (for example keyed them in by hand).",
    "detail.action.dontSend": "Don't send",
    "detail.action.dontSendHint": "Use this when the worker should not go to the payroll system at all.",
    "detail.toast.sending": "Sending the worker now",
    "detail.toast.checkingWorkOrder": "Checking the work order again",
    "detail.toast.tryingAgain": "Trying again",
    "detail.toast.markedHandled": "Marked as handled",
    "detail.toast.willNotSend": "This worker will not be sent",
    "detail.toast.actionFailed": "That didn't work",

    // Edit details
    "editDetails.title": "Edit {name}'s details",
    "editDetails.fallbackWorker": "worker #{id}",
    "editDetails.description":
      "Fix or fill in what will be sent to {provider}. Saving here does not send anything until you choose \"Save and send now\" — and once you have saved, FBO keeps your version instead of rebuilding the details automatically.",
    "editDetails.noPayload.typeNotEnabled":
      "This worker's type is not enabled for {provider}, so their details were never prepared — tick the type under “Which workers are sent” on the Payroll systems tab, then use “Try again”.",
    "editDetails.noPayload.multipleOrgs":
      "This worker has work orders under more than one of your organizations, so their details were never prepared — resolve the work orders (or turn on “one record per internal organization” on the Payroll systems tab once the provider confirms that setup), then use “Try again”.",
    "editDetails.noPayload.noProvider":
      "No payroll provider is set up and enabled for this tenant yet, so there is nothing to edit — configure one on the Payroll systems tab, then use “Try again”.",
    "editDetails.noPayload.waitingForWorkOrder":
      "This worker has no work order yet, so their details have not been prepared — they are prepared automatically when a work order is approved. Nothing to edit until then.",
    "editDetails.noPayload.generic":
      "This worker's details have not been prepared yet, so there is nothing to edit — use “Try again” first and come back once the details appear.",
    "editDetails.group.whoTheyAre": "Who they are",
    "editDetails.group.howToReach": "How to reach them",
    "editDetails.group.theirWork": "Their work",
    "editDetails.group.address": "Address",
    "editDetails.group.orgCodes": "Organization and provider codes",
    "editDetails.hint.providerEmployeeId":
      "The number that identifies this worker in the payroll system. It comes from the worker's payment method in FBO — fixing it there is better than overriding it here.",
    "editDetails.hint.lastName":
      "For a company, the legal name is split across the name fields when it is too long, and \"Inc\" stays at the end here.",
    "editDetails.hint.middleName": "Sent to the payroll system when present.",
    "editDetails.hint.middleNameSplit":
      "Words that do not fit in the first or last name are moved here automatically.",
    "editDetails.hint.hireDate": "Usually the work order's start date.",
    "editDetails.hint.payType": "e.g. Hourly",
    "editDetails.hint.workLocation":
      "The state where the work is done — the worker's for remote, the client site otherwise.",
    "editDetails.hint.ein":
      "Companies only: the organization's EIN or business number. Fixing it on the organization's role in FBO is better than overriding it here.",
    "editDetails.maxLengthHint": "Up to {count} characters.",
    "editDetails.internalOrganization": "Internal organization",
    "editDetails.orgHint": "Choosing one fills in the codes from the Providers tab.",
    "editDetails.carriedThrough":
      "Standard values and organization assignments come from the Payroll systems tab and are carried through unchanged.",
    "editDetails.saveAndSend": "Save and send now",
    "editDetails.saveForLater": "Save for later",
    "editDetails.toast.savedAndSending": "Details saved — sending the worker now",
    "editDetails.toast.saved": "Details saved",
    "editDetails.toast.savedDescription": "Nothing was sent — use “Send now” when the worker is ready.",
    "editDetails.toast.sendFailed": "The worker could not be sent",
    "editDetails.toast.saveFailed": "The details could not be saved",

    // Payload field labels
    "field.userProfileId": "FBO profile number (UserProfileId)",
    "field.providerEmployeeId": "Employee ID (employee number)",
    "field.category": "Worker category",
    "field.firstName": "First name",
    "field.lastName": "Last name",
    "field.middleName": "Middle name",
    "field.email": "Email",
    "field.profileTypeValue": "Worker type",
    "field.profileTypeCode": "Worker type (internal code)",
    "field.hireDate": "Start date",
    "field.payType": "Pay type",
    "field.hourlyRate": "Hourly rate",
    "field.normalHours": "Hours per day",
    "field.department": "Department",
    "field.workersCompCode": "Workers' comp code",
    "field.workLocation": "Work location",
    "field.ein": "EIN / business number (companies only)",
    "field.internalOrganizationId": "Internal organization (FBO ID)",
    "field.internalOrganizationCode": "Organization code",
    "field.providerClientId": "Provider client ID",
    "field.legalCode": "Legal code",
    "field.payGroup": "Pay group",
    "field.jobTitle": "Job title",
    "field.address1": "Address line 1",
    "field.address2": "Address line 2",
    "field.city": "City",
    "field.provinceOrState": "Province / State",
    "field.postalOrZip": "Postal / ZIP code",
    "field.countryCode": "Country",
    "field.homePhone": "Home phone",
    "field.mobilePhone": "Mobile phone",
    "field.additionalFields": "Standard values (constant fields)",
    "field.organizationFields": "Organization assignments (provider organizations)",

    // Backfill
    "backfill.intro":
      "Active workers who are not being sent to {provider} yet — usually because they were approved before sending was turned on. Pick the ones to add; they then show up on the Workers tab like any newly approved worker.",
    "backfill.yourPayrollSystem": "your payroll system",
    "backfill.error.load": "The list of workers could not be loaded.",
    "backfill.searchPlaceholder": "Filter by worker name",
    "backfill.limitNotice":
      "Showing {count} of the workers that can still be added — the list moves on to the rest as these are added.",
    "backfill.filter.anyStatus": "Any status",
    "backfill.filter.allTypes": "All types",
    "backfill.filter.byWorkerTypeAria": "Filter by worker type",
    "backfill.filter.byStatusAria": "Filter by status",
    "backfill.status.ready": "Ready to add",
    "backfill.status.typeNotSent": "Type not sent",
    "backfill.status.typeNotSentLong": "Type not sent — will show an error",
    "backfill.status.typeNotSentHint":
      "This worker type is not ticked under “Which workers are sent” on the Providers tab. Adding the worker will show an error there until the type is ticked.",
    "backfill.selectAllReady": "Select all that are ready to add",
    "backfill.selectWorkerAria": "Select {name}",
    "backfill.addSelected": "Add selected workers",
    "backfill.addSelectedOne": "Add 1 selected worker",
    "backfill.addSelectedCount": "Add {count} selected workers",
    "backfill.toast.queuedOne": "1 worker added to the queue",
    "backfill.toast.queued": "{count} workers added to the queue",
    "backfill.toast.queuedDescription": "Watch their progress on the Workers tab.",
    "backfill.toast.addFailed": "The workers could not be added",

    // Value translations
    "mappings.intro":
      "Translate FBO's internal values into the words your payroll system expects. Leave a list empty to send FBO's value unchanged.",
    "mappings.error.load": "The translations could not be loaded.",
    "mappings.hint.department": "FBO sends the job category number unless you translate it here.",
    "mappings.hint.workersCompCode": "FBO sends its internal code number unless you translate it here.",
    "mappings.hint.workLocation":
      "FBO sends the two-letter state (IL) unless your payroll system wants its own location codes — translate them here.",
    "mappings.header.fboValue": "Value in FBO",
    "mappings.header.providerValue": "What {provider} should receive",
    "mappings.header.note": "Note (optional)",
    "mappings.aria.fboValue": "FBO value",
    "mappings.aria.providerValue": "Payroll system value",
    "mappings.aria.note": "Note",
    "mappings.aria.removeLine": "Remove line",
    "mappings.placeholder.chooseValue": "Choose a value",
    "mappings.placeholder.fboValue": "e.g. 12",
    "mappings.placeholder.providerValue": "e.g. Engineering",
    "mappings.placeholder.note": "What this value is",
    "mappings.addLine": "Add a line",
    "mappings.notListedNote":
      "A worker whose value is not on this list shows as \"Needs attention\" on the Workers tab — nothing is guessed.",
    "mappings.toast.incompleteLine": "Every line needs both an FBO value and a payroll-system value",
    "mappings.toast.duplicateValue": "The FBO value \"{value}\" appears more than once",
    "mappings.toast.duplicateValueDescription":
      "Each FBO value can have only one translation — remove the duplicate line.",
    "mappings.toast.saved": "{field} translations saved",
    "mappings.toast.saveFailed": "The translations could not be saved",

    // Providers
    "providers.oneSetPerProvider": "One set of settings per provider",
    "providers.sending": "Sending",
    "providers.notSetUp": "Not set up",
    "providers.error.load": "The provider settings could not be loaded.",
    "providers.toast.settingsSaved": "Settings saved",
    "providers.headerDescription": "Everything about sending workers to {provider}, saved together.",
    "providers.testConnection": "Test the connection",
    "providers.testSuccess": "Connected to {provider} successfully.",
    "providers.toast.chooseEnvironment":
      "Choose where to send workers (test or live) before turning sending on",
    "providers.toast.saveFailed": "The settings could not be saved",
    "providers.sendingLabel": "Sending",
    "providers.sendTo": "Send to (provider environment)",
    "providers.baseUrl": "Service address (API base URL)",
    "providers.baseUrlHint": "Provided by {provider}. Only change it if they tell you to.",
    "providers.clientIdLabel": "{provider} client ID",
    "providers.clientIdHint": "The client number {provider} gave your company.",
    "providers.clientIdPlaceholder": "e.g. 241",
    "providers.paymentFormat.title": "Payment file format",
    "providers.paymentFormat.hint":
      "Makes {provider} selectable as a payment file format in this tenant. Which of your companies uses which format is still chosen on the organization's payment method screen.",
    "providers.paymentFormat.available": "Available in this tenant",
    "providers.paymentFormat.default": "Use as the default format",
    "providers.orgs.title": "Your organizations (internal organization mapping)",
    "providers.orgs.add": "Add an organization",
    "providers.orgs.hint":
      "Match each of your internal organizations to the codes {provider} uses. A worker whose organization is not listed here (or is marked \"don't send\") shows up as an error on the Workers tab — nothing is skipped quietly.",
    "providers.orgs.empty": "No organizations added yet.",
    "providers.orgs.header.fboId": "FBO org ID",
    "providers.orgs.header.orgCode": "Org code",
    "providers.orgs.header.orgName": "Organization name",
    "providers.orgs.header.clientId": "Client ID (opt.)",
    "providers.orgs.aria.fboId": "FBO organization id",
    "providers.orgs.aria.clientIdOverride": "Provider client ID override",
    "providers.orgs.aria.remove": "Remove organization",
    "providers.orgs.placeholder.fboId": "e.g. 1",
    "providers.orgs.placeholder.orgCode": "e.g. HA",
    "providers.orgs.placeholder.usesDefault": "Uses default",
    "providers.orgs.placeholder.legalCode": "e.g. 1670",
    "providers.orgs.dontSend": "Don't send",
    "providers.types.title": "Which workers are sent (profile types)",
    "providers.types.hint":
      "Tick the worker types that should go to {provider}. Workers of other types are reported as errors on the Workers tab so nothing goes missing unnoticed.",
    "providers.types.notInList": "{code} (not in the current type list)",
    "providers.how.title": "How workers are sent",
    "providers.how.perOrg": "One record per internal organization the worker works under",
    "providers.how.c2cLastName": "Last name for incorporated (C2C) workers",
    "providers.how.hint":
      "Leave \"one record per internal organization\" off unless {provider} has confirmed that setup — while off, a worker working under two of your organizations is reported as an error instead of guessed. For incorporated (C2C) workers the first name carries the organization’s full legal name and the last name is the word set here.",
    "providers.extras.title": "Extra details from the work order",
    "providers.extras.hint":
      "Usually left off — hours and pay arrive with the time import. Turn these on only if {provider} asks for them.",
    "providers.standard.hint":
      "Details {provider} expects on every worker that FBO does not store — for example the ID type or employment category. Type true/false or a number to send it as that type.",
    "providers.standard.aria.fieldName": "{audience} field name",
    "providers.standard.aria.fieldValue": "{audience} field value",
    "providers.standard.placeholder.name": "Field name (e.g. idType)",
    "providers.standard.placeholder.value": "Value (e.g. SSN)",
    "providers.standard.removeValue": "Remove value",
    "providers.standard.addValue": "Add a value",
    "providers.standard.addOrgAssignment": "Add an organization assignment",
    "providers.standard.orgAssignmentsHint":
      "Organization assignments — {provider}'s own grouping fields (for example Division).",
    "providers.standard.aria.assignmentTitle": "Assignment title",
    "providers.standard.aria.assignmentValue": "Assignment value",
    "providers.standard.aria.assignmentNote": "Assignment note",
    "providers.standard.removeAssignment": "Remove assignment",
    "providers.standard.placeholder.assignmentTitle": "Title (e.g. Division)",
    "providers.standard.placeholder.assignmentValue": "Value (e.g. Consultant)",
    "providers.audience.all.label": "Everyone (All)",
    "providers.audience.all.hint": "Included for every worker sent.",
    "providers.audience.individual.label": "Individuals (employees, e.g. W2)",
    "providers.audience.individual.hint": "Overrides “Everyone” for workers paid as a person.",
    "providers.audience.organization.label": "Organizations (incorporated / C2C)",
    "providers.audience.organization.hint":
      "Overrides “Everyone” for workers paid through their own organization.",
    "providers.triggers.title": "When workers are sent automatically",
    "providers.triggers.onApproval": "When their profile is approved",
    "providers.triggers.onChange": "When their details change (coming later)",
    "providers.triggers.onTermination": "When they are terminated (coming later)",
    "providers.triggers.reconcile":
      "Catch workers that were missed (reconciliation — checks every few hours)",
    "providers.triggers.hint":
      "\"Catch workers that were missed\" automatically sends any active worker of a ticked type who is not on the Workers tab yet. Leave it off until you have added your existing workers on the \"Add existing workers\" tab — otherwise it sends every historical worker on its own.",

    // Credentials
    "credentials.title": "Sign-in details for {provider} (API OAuth credentials)",
    "credentials.hint":
      "The account {provider} gave your company for this connection — not your own FBO login. Saved values are encrypted and never shown again; the fields below stay blank on purpose. Enter a value only to set it or replace it.",
    "credentials.lastChanged": "Last changed {date}.",
    "credentials.lastChangedBy": "Last changed {date} by {name}.",
    "credentials.environmentLabel": "These details are for (provider environment)",
    "credentials.clientId": "Client ID",
    "credentials.clientSecret": "Client secret",
    "credentials.placeholderSaved": "•••••••• (saved)",
    "credentials.placeholderUnset": "Not set yet",
    "credentials.save": "Save sign-in details",
    "credentials.toast.nothingEntered": "Enter a new value in at least one field first",
    "credentials.toast.saved": "Sign-in details saved",
    "credentials.toast.savedDescription": "They are stored encrypted and cannot be read back.",
    "credentials.toast.saveFailed": "The sign-in details could not be saved",
  },

  // French copy is a starting draft — have a fluent reviewer pass over it before shipping.
  fr: {
    // Common
    "common.skipToMain": "Passer au contenu principal",
    "common.save": "Enregistrer",
    "common.cancel": "Annuler",
    "common.close": "Fermer",
    "common.done": "Terminé",
    "common.tryAgain": "Réessayer",
    "common.open": "Ouvrir",
    "common.loading": "Chargement…",
    "common.refresh": "Actualiser",
    "common.filters": "Filtres",
    "common.activeFilters": "Actifs",
    "common.clearFilters": "Effacer les filtres",
    "common.clearAll": "Tout effacer",
    "common.removeFilterAria": "Retirer le filtre : {label}",
    "common.sortPrefix": "Tri :",
    "common.sortByAria": "Trier par",
    "common.sortedAscending": "Tri croissant — passer au tri décroissant",
    "common.sortedDescending": "Tri décroissant — passer au tri croissant",
    "common.all": "Tous",
    "common.any": "Tous",
    "common.status": "Statut",
    "common.from": "Du",
    "common.upTo": "Jusqu'au",
    "common.on": "Activé",
    "common.off": "Désactivé",
    "common.notSet": "Non défini",
    "common.added": "Ajouté",
    "common.payrollSystem": "Système de paie",
    "common.chooseOrganization": "Choisir une organisation",
    "common.notInCurrentListValue": "{value} (absent de la liste actuelle)",
    "common.env.sandbox": "Système d'essai (sandbox)",
    "common.env.production": "Système réel (production)",
    "common.env.live": "réel",

    // Pager
    "pager.showing": "Affichage de {first} à {last} sur {total}",
    "pager.perPage": "Par page",
    "pager.rowsPerPageAria": "Lignes par page",
    "pager.previousPage": "Page précédente",
    "pager.nextPage": "Page suivante",
    "pager.pageOf": "Page {page} sur {pageCount}",

    // Page shell
    "page.title": "Intégrations de paie",
    // Sits beside the title in the header band, so it stays to one line.
    "page.subtitle": "Travailleurs, systèmes de paie, traductions et ajouts en un seul endroit",
    "tabs.workers": "Travailleurs (migrations)",
    "tabs.providers": "Systèmes de paie (fournisseurs)",
    "tabs.mappings": "Traductions de valeurs (mappings)",
    "tabs.backfill": "Ajouter les travailleurs existants (backfill)",
    // Captions beneath the figure on each hub card.
    "hub.tablistAria": "Sections des intégrations de paie",
    "hub.workers.sub": "travailleurs suivis",
    "hub.providers.noneYet": "aucun envoi pour l'instant",
    "hub.mappings.sub": "listes de traduction utilisées",
    "hub.backfill.sub": "pas encore ajoutés",
    "notFound.message": "Page introuvable.",

    // Workers
    "workers.intro":
      "Chaque travailleur envoyé à votre système de paie, et où il en est. Ouvrez un travailleur pour consulter ou corriger ses détails.",
    "workers.error.load":
      "La liste n'a pas pu être chargée. Vérifiez que le service Intégrations de paie est accessible.",
    "workers.toast.detailLoadFailed": "Les détails du travailleur n'ont pas pu être chargés",
    "workers.card.all": "Tous les travailleurs",
    "workers.card.needsAttention": "Nécessite une attention",
    "workers.card.addedToPayroll": "Ajouté à la paie",
    "workers.searchPlaceholder": "Rechercher par nom de travailleur",
    "workers.limitNotice":
      "Seuls les {count} travailleurs correspondants les plus récents sont affichés — précisez la recherche, le filtre de progression ou la plage de dates pour voir les autres.",
    "workers.chip.addedFrom": "Ajouté à partir du {date}",
    "workers.chip.addedTo": "Ajouté jusqu'au {date}",
    "workers.filter.progress": "Progression",
    "workers.filter.addedBetween": "Ajouté entre",
    "workers.filter.byPayrollSystemAria": "Filtrer par système de paie",
    "workers.filter.byProgressAria": "Filtrer par progression",
    "workers.countOf": "{shown} travailleurs sur {total}",
    "workers.empty": "Aucun travailleur ne correspond aux filtres actuels.",
    "workers.addedOn": "Ajouté le {date}",
    "workers.profileNumber": "Profil {id}",
    "workers.meta.profile": "Profil",
    "workers.meta.employeeId": "Numéro d'employé",
    "workers.meta.sentTo": "Envoyé vers",
    "workers.meta.tries": "Tentatives",
    "workers.meta.whatHappened": "Ce qui s'est passé",
    "workers.sort.worker": "Travailleur",
    "workers.sort.organization": "Organisation",
    "workers.status.pending": "En préparation (Pending)",
    "workers.status.waitingForWorkOrder": "En attente d'un ordre de travail",
    "workers.status.sent": "Envoi en cours (Sent)",
    "workers.status.created": "Ajouté à la paie (Created)",
    "workers.status.failed": "Nécessite une attention (Failed)",
    "workers.status.skipped": "Non traité (Skipped)",
    "workers.status.ignored": "Écarté (Ignored)",
    "workers.status.resolved": "Traité manuellement (Resolved)",

    // Worker detail
    "detail.goingToLive": "Destiné à {provider} (réel)",
    "detail.goingToEnv": "Destiné à {provider} ({environment})",
    "detail.meta.fboProfile": "Profil FBO",
    "detail.meta.employeeIdAt": "Numéro d'employé chez {provider}",
    "detail.meta.triesSoFar": "Tentatives à ce jour",
    "detail.meta.accepted": "Accepté le",
    "detail.meta.lastActionBy": "Dernière action par",
    "detail.meta.handledBy": "Traité par",
    "detail.acceptedOn": "{provider} a accepté ce travailleur le {date} — rien de plus à faire ici.",
    "detail.acceptedNoDate": "{provider} a accepté ce travailleur — rien de plus à faire ici.",
    "detail.whatWentWrong": "Ce qui n'a pas fonctionné",
    "detail.whyWaiting": "Pourquoi ce travailleur est en attente",
    "detail.gateHint":
      "L'envoi se fera automatiquement dès que l'ordre de travail sera en place — ou utilisez \"Modifier les détails\" pour remplir ce qui manque, puis \"Envoyer maintenant\" pour l'envoyer quand même.",
    "detail.advanced": "Avancé : les données exactes échangées",
    "detail.sentHeading": "Ce que FBO a envoyé à {provider}",
    "detail.wouldSendHeading": "Ce que FBO enverrait à {provider}",
    "detail.noStoredBodySent":
      "Une création a été transmise à {provider}, mais aucun corps de requête n'a été conservé — cet enregistrement est antérieur à leur conservation par FBO.",
    "detail.noStoredBodyCreated":
      "Aucun corps de requête n'a été conservé pour ce travailleur — soit l'enregistrement est antérieur à leur conservation par FBO, soit le travailleur existait déjà chez {provider} et aucune création n'a été envoyée.",
    "detail.workingOutPreview": "Calcul de ce qui serait envoyé…",
    "detail.previewWouldSendNow": "Voici ce que ce même travailleur enverrait maintenant.",
    "detail.previewNothingSentYet": "Rien n'a encore été envoyé — voici ce qui partirait.",
    "detail.noSentBodyRecorded": "Aucun corps de requête n'a été conservé pour ce travailleur.",
    "detail.nothingSentYet": "Rien n'a encore été envoyé.",
    "detail.previewFailed": "Impossible de déterminer ce qui serait envoyé.",
    "detail.answerHeading": "Ce que {provider} a répondu",
    "detail.reference": "Référence {reference}",
    "detail.sentOn": "Envoyé le {date}",
    "detail.action.sendNow": "Envoyer maintenant",
    "detail.action.checkWorkOrder": "Vérifier de nouveau l'ordre de travail",
    "detail.action.editDetails": "Modifier les détails",
    "detail.action.markHandled": "Marquer comme traité",
    "detail.action.markHandledHint":
      "À utiliser lorsque vous avez traité le travailleur en dehors de FBO (par exemple en le saisissant à la main).",
    "detail.action.dontSend": "Ne pas envoyer",
    "detail.action.dontSendHint":
      "À utiliser lorsque le travailleur ne doit pas du tout être envoyé au système de paie.",
    "detail.toast.sending": "Envoi du travailleur en cours",
    "detail.toast.checkingWorkOrder": "Nouvelle vérification de l'ordre de travail",
    "detail.toast.tryingAgain": "Nouvelle tentative",
    "detail.toast.markedHandled": "Marqué comme traité",
    "detail.toast.willNotSend": "Ce travailleur ne sera pas envoyé",
    "detail.toast.actionFailed": "Cela n'a pas fonctionné",

    // Edit details
    "editDetails.title": "Modifier les détails de {name}",
    "editDetails.fallbackWorker": "travailleur no {id}",
    "editDetails.description":
      "Corrigez ou complétez ce qui sera envoyé à {provider}. Enregistrer ici n'envoie rien tant que vous ne choisissez pas \"Enregistrer et envoyer maintenant\" — et une fois l'enregistrement fait, FBO conserve votre version au lieu de reconstruire les détails automatiquement.",
    "editDetails.noPayload.typeNotEnabled":
      "Le type de ce travailleur n'est pas activé pour {provider}, ses détails n'ont donc jamais été préparés — cochez le type sous \"Quels travailleurs sont envoyés\" dans l'onglet Systèmes de paie, puis utilisez \"Réessayer\".",
    "editDetails.noPayload.multipleOrgs":
      "Ce travailleur a des ordres de travail sous plus d'une de vos organisations, ses détails n'ont donc jamais été préparés — réglez les ordres de travail (ou activez \"un enregistrement par organisation interne\" dans l'onglet Systèmes de paie une fois que le fournisseur a confirmé cette configuration), puis utilisez \"Réessayer\".",
    "editDetails.noPayload.noProvider":
      "Aucun fournisseur de paie n'est encore configuré et activé pour ce locataire, il n'y a donc rien à modifier — configurez-en un dans l'onglet Systèmes de paie, puis utilisez \"Réessayer\".",
    "editDetails.noPayload.waitingForWorkOrder":
      "Ce travailleur n'a pas encore d'ordre de travail, ses détails n'ont donc pas été préparés — ils le sont automatiquement lorsqu'un ordre de travail est approuvé. Rien à modifier d'ici là.",
    "editDetails.noPayload.generic":
      "Les détails de ce travailleur ne sont pas encore préparés, il n'y a donc rien à modifier — utilisez d'abord \"Réessayer\", puis revenez lorsque les détails apparaissent.",
    "editDetails.group.whoTheyAre": "Qui il est",
    "editDetails.group.howToReach": "Comment le joindre",
    "editDetails.group.theirWork": "Son travail",
    "editDetails.group.address": "Adresse",
    "editDetails.group.orgCodes": "Codes d'organisation et du fournisseur",
    "editDetails.hint.providerEmployeeId":
      "Le numéro qui identifie ce travailleur dans le système de paie. Il provient du mode de paiement du travailleur dans FBO — il est préférable de le corriger là plutôt que de le remplacer ici.",
    "editDetails.hint.lastName":
      "Pour une entreprise, la dénomination sociale est répartie entre les champs de nom lorsqu'elle est trop longue, et \"Inc\" reste à la fin ici.",
    "editDetails.hint.middleName": "Envoyé au système de paie lorsqu'il est présent.",
    "editDetails.hint.middleNameSplit":
      "Les mots qui ne tiennent pas dans le prénom ou le nom sont déplacés ici automatiquement.",
    "editDetails.hint.hireDate": "Habituellement la date de début de l'ordre de travail.",
    "editDetails.hint.payType": "p. ex. Horaire",
    "editDetails.hint.workLocation":
      "L'État où le travail est effectué — celui du travailleur en télétravail, sinon le site du client.",
    "editDetails.hint.ein":
      "Entreprises seulement : le EIN ou le numéro d'entreprise de l'organisation. Il est préférable de le corriger sur le rôle de l'organisation dans FBO plutôt que de le remplacer ici.",
    "editDetails.maxLengthHint": "Jusqu'à {count} caractères.",
    "editDetails.internalOrganization": "Organisation interne",
    "editDetails.orgHint": "En choisir une remplit les codes provenant de l'onglet Fournisseurs.",
    "editDetails.carriedThrough":
      "Les valeurs standards et les affectations d'organisation proviennent de l'onglet Systèmes de paie et sont transmises telles quelles.",
    "editDetails.saveAndSend": "Enregistrer et envoyer maintenant",
    "editDetails.saveForLater": "Enregistrer pour plus tard",
    "editDetails.toast.savedAndSending": "Détails enregistrés — envoi du travailleur en cours",
    "editDetails.toast.saved": "Détails enregistrés",
    "editDetails.toast.savedDescription":
      "Rien n'a été envoyé — utilisez \"Envoyer maintenant\" lorsque le travailleur est prêt.",
    "editDetails.toast.sendFailed": "Le travailleur n'a pas pu être envoyé",
    "editDetails.toast.saveFailed": "Les détails n'ont pas pu être enregistrés",

    // Payload field labels
    "field.userProfileId": "Numéro de profil FBO (UserProfileId)",
    "field.providerEmployeeId": "Numéro d'employé (employee number)",
    "field.category": "Catégorie de travailleur",
    "field.firstName": "Prénom",
    "field.lastName": "Nom",
    "field.middleName": "Second prénom",
    "field.email": "Courriel",
    "field.profileTypeValue": "Type de travailleur",
    "field.profileTypeCode": "Type de travailleur (code interne)",
    "field.hireDate": "Date de début",
    "field.payType": "Type de paie",
    "field.hourlyRate": "Taux horaire",
    "field.normalHours": "Heures par jour",
    "field.department": "Département",
    "field.workersCompCode": "Code d'indemnisation des travailleurs",
    "field.workLocation": "Lieu de travail",
    "field.ein": "EIN / numéro d'entreprise (entreprises seulement)",
    "field.internalOrganizationId": "Organisation interne (ID FBO)",
    "field.internalOrganizationCode": "Code d'organisation",
    "field.providerClientId": "ID client du fournisseur",
    "field.legalCode": "Code légal",
    "field.payGroup": "Groupe de paie",
    "field.jobTitle": "Titre du poste",
    "field.address1": "Adresse ligne 1",
    "field.address2": "Adresse ligne 2",
    "field.city": "Ville",
    "field.provinceOrState": "Province / État",
    "field.postalOrZip": "Code postal / ZIP",
    "field.countryCode": "Pays",
    "field.homePhone": "Téléphone à domicile",
    "field.mobilePhone": "Téléphone mobile",
    "field.additionalFields": "Valeurs standards (constant fields)",
    "field.organizationFields": "Affectations d'organisation (provider organizations)",

    // Backfill
    "backfill.intro":
      "Les travailleurs actifs qui ne sont pas encore envoyés à {provider} — généralement parce qu'ils ont été approuvés avant l'activation de l'envoi. Choisissez ceux à ajouter; ils apparaissent ensuite dans l'onglet Travailleurs comme tout travailleur nouvellement approuvé.",
    "backfill.yourPayrollSystem": "votre système de paie",
    "backfill.error.load": "La liste des travailleurs n'a pas pu être chargée.",
    "backfill.searchPlaceholder": "Filtrer par nom de travailleur",
    "backfill.limitNotice":
      "Affichage de {count} des travailleurs qui peuvent encore être ajoutés — la liste passe aux suivants à mesure que ceux-ci sont ajoutés.",
    "backfill.filter.anyStatus": "Tout statut",
    "backfill.filter.allTypes": "Tous les types",
    "backfill.filter.byWorkerTypeAria": "Filtrer par type de travailleur",
    "backfill.filter.byStatusAria": "Filtrer par statut",
    "backfill.status.ready": "Prêt à ajouter",
    "backfill.status.typeNotSent": "Type non envoyé",
    "backfill.status.typeNotSentLong": "Type non envoyé — affichera une erreur",
    "backfill.status.typeNotSentHint":
      "Ce type de travailleur n'est pas coché sous \"Quels travailleurs sont envoyés\" dans l'onglet Fournisseurs. L'ajout du travailleur y affichera une erreur jusqu'à ce que le type soit coché.",
    "backfill.selectAllReady": "Sélectionner tous ceux qui sont prêts à ajouter",
    "backfill.selectWorkerAria": "Sélectionner {name}",
    "backfill.addSelected": "Ajouter les travailleurs sélectionnés",
    "backfill.addSelectedOne": "Ajouter 1 travailleur sélectionné",
    "backfill.addSelectedCount": "Ajouter {count} travailleurs sélectionnés",
    "backfill.toast.queuedOne": "1 travailleur ajouté à la file",
    "backfill.toast.queued": "{count} travailleurs ajoutés à la file",
    "backfill.toast.queuedDescription": "Suivez leur progression dans l'onglet Travailleurs.",
    "backfill.toast.addFailed": "Les travailleurs n'ont pas pu être ajoutés",

    // Value translations
    "mappings.intro":
      "Traduisez les valeurs internes de FBO dans les termes attendus par votre système de paie. Laissez une liste vide pour envoyer la valeur de FBO telle quelle.",
    "mappings.error.load": "Les traductions n'ont pas pu être chargées.",
    "mappings.hint.department":
      "FBO envoie le numéro de catégorie d'emploi à moins que vous ne le traduisiez ici.",
    "mappings.hint.workersCompCode":
      "FBO envoie son numéro de code interne à moins que vous ne le traduisiez ici.",
    "mappings.hint.workLocation":
      "FBO envoie l'État à deux lettres (IL) à moins que votre système de paie n'exige ses propres codes de lieu — traduisez-les ici.",
    "mappings.header.fboValue": "Valeur dans FBO",
    "mappings.header.providerValue": "Ce que {provider} doit recevoir",
    "mappings.header.note": "Note (facultatif)",
    "mappings.aria.fboValue": "Valeur FBO",
    "mappings.aria.providerValue": "Valeur du système de paie",
    "mappings.aria.note": "Note",
    "mappings.aria.removeLine": "Supprimer la ligne",
    "mappings.placeholder.chooseValue": "Choisir une valeur",
    "mappings.placeholder.fboValue": "p. ex. 12",
    "mappings.placeholder.providerValue": "p. ex. Ingénierie",
    "mappings.placeholder.note": "Ce que représente cette valeur",
    "mappings.addLine": "Ajouter une ligne",
    "mappings.notListedNote":
      "Un travailleur dont la valeur n'est pas dans cette liste apparaît comme \"Nécessite une attention\" dans l'onglet Travailleurs — rien n'est deviné.",
    "mappings.toast.incompleteLine":
      "Chaque ligne exige à la fois une valeur FBO et une valeur du système de paie",
    "mappings.toast.duplicateValue": "La valeur FBO \"{value}\" apparaît plus d'une fois",
    "mappings.toast.duplicateValueDescription":
      "Chaque valeur FBO ne peut avoir qu'une seule traduction — supprimez la ligne en double.",
    "mappings.toast.saved": "Traductions de {field} enregistrées",
    "mappings.toast.saveFailed": "Les traductions n'ont pas pu être enregistrées",

    // Providers
    "providers.oneSetPerProvider": "Un ensemble de paramètres par fournisseur",
    "providers.sending": "Envoi actif",
    "providers.notSetUp": "Non configuré",
    "providers.error.load": "Les paramètres du fournisseur n'ont pas pu être chargés.",
    "providers.toast.settingsSaved": "Paramètres enregistrés",
    "providers.headerDescription":
      "Tout ce qui concerne l'envoi des travailleurs à {provider}, enregistré ensemble.",
    "providers.testConnection": "Tester la connexion",
    "providers.testSuccess": "Connexion à {provider} réussie.",
    "providers.toast.chooseEnvironment":
      "Choisissez où envoyer les travailleurs (essai ou réel) avant d'activer l'envoi",
    "providers.toast.saveFailed": "Les paramètres n'ont pas pu être enregistrés",
    "providers.sendingLabel": "Envoi",
    "providers.sendTo": "Envoyer vers (environnement du fournisseur)",
    "providers.baseUrl": "Adresse du service (API base URL)",
    "providers.baseUrlHint": "Fournie par {provider}. Ne la modifiez que sur leur demande.",
    "providers.clientIdLabel": "ID client {provider}",
    "providers.clientIdHint": "Le numéro de client que {provider} a attribué à votre entreprise.",
    "providers.clientIdPlaceholder": "p. ex. 241",
    "providers.paymentFormat.title": "Format de fichier de paiement",
    "providers.paymentFormat.hint":
      "Rend {provider} sélectionnable comme format de fichier de paiement dans ce locataire. Le format utilisé par chacune de vos entreprises se choisit toujours dans l'écran du mode de paiement de l'organisation.",
    "providers.paymentFormat.available": "Disponible dans ce locataire",
    "providers.paymentFormat.default": "Utiliser comme format par défaut",
    "providers.orgs.title": "Vos organisations (mappage des organisations internes)",
    "providers.orgs.add": "Ajouter une organisation",
    "providers.orgs.hint":
      "Associez chacune de vos organisations internes aux codes utilisés par {provider}. Un travailleur dont l'organisation n'est pas listée ici (ou est marquée \"ne pas envoyer\") apparaît comme une erreur dans l'onglet Travailleurs — rien n'est ignoré silencieusement.",
    "providers.orgs.empty": "Aucune organisation ajoutée pour le moment.",
    "providers.orgs.header.fboId": "ID org FBO",
    "providers.orgs.header.orgCode": "Code org",
    "providers.orgs.header.orgName": "Nom de l'organisation",
    "providers.orgs.header.clientId": "ID client (fac.)",
    "providers.orgs.aria.fboId": "ID de l'organisation FBO",
    "providers.orgs.aria.clientIdOverride": "Remplacement de l'ID client du fournisseur",
    "providers.orgs.aria.remove": "Supprimer l'organisation",
    "providers.orgs.placeholder.fboId": "p. ex. 1",
    "providers.orgs.placeholder.orgCode": "p. ex. HA",
    "providers.orgs.placeholder.usesDefault": "Utilise la valeur par défaut",
    "providers.orgs.placeholder.legalCode": "p. ex. 1670",
    "providers.orgs.dontSend": "Ne pas envoyer",
    "providers.types.title": "Quels travailleurs sont envoyés (types de profil)",
    "providers.types.hint":
      "Cochez les types de travailleurs qui doivent être envoyés à {provider}. Les travailleurs d'autres types sont signalés comme des erreurs dans l'onglet Travailleurs, afin que rien ne passe inaperçu.",
    "providers.types.notInList": "{code} (absent de la liste de types actuelle)",
    "providers.how.title": "Comment les travailleurs sont envoyés",
    "providers.how.perOrg":
      "Un enregistrement par organisation interne pour laquelle le travailleur travaille",
    "providers.how.c2cLastName": "Nom de famille pour les travailleurs incorporés (C2C)",
    "providers.how.hint":
      "Laissez \"un enregistrement par organisation interne\" désactivé à moins que {provider} n'ait confirmé cette configuration — lorsque l'option est désactivée, un travailleur qui travaille pour deux de vos organisations est signalé comme une erreur plutôt que deviné. Pour les travailleurs incorporés (C2C), le prénom porte la dénomination sociale complète de l'organisation et le nom de famille est le mot défini ici.",
    "providers.extras.title": "Détails supplémentaires provenant de l'ordre de travail",
    "providers.extras.hint":
      "Habituellement désactivés — les heures et la paie arrivent avec l'importation des feuilles de temps. Ne les activez que si {provider} les demande.",
    "providers.standard.hint":
      "Les détails que {provider} attend pour chaque travailleur et que FBO ne conserve pas — par exemple le type de pièce d'identité ou la catégorie d'emploi. Saisissez true/false ou un nombre pour l'envoyer comme tel.",
    "providers.standard.aria.fieldName": "Nom du champ — {audience}",
    "providers.standard.aria.fieldValue": "Valeur du champ — {audience}",
    "providers.standard.placeholder.name": "Nom du champ (p. ex. idType)",
    "providers.standard.placeholder.value": "Valeur (p. ex. SSN)",
    "providers.standard.removeValue": "Supprimer la valeur",
    "providers.standard.addValue": "Ajouter une valeur",
    "providers.standard.addOrgAssignment": "Ajouter une affectation d'organisation",
    "providers.standard.orgAssignmentsHint":
      "Affectations d'organisation — les champs de regroupement propres à {provider} (par exemple Division).",
    "providers.standard.aria.assignmentTitle": "Titre de l'affectation",
    "providers.standard.aria.assignmentValue": "Valeur de l'affectation",
    "providers.standard.aria.assignmentNote": "Note de l'affectation",
    "providers.standard.removeAssignment": "Supprimer l'affectation",
    "providers.standard.placeholder.assignmentTitle": "Titre (p. ex. Division)",
    "providers.standard.placeholder.assignmentValue": "Valeur (p. ex. Consultant)",
    "providers.audience.all.label": "Tout le monde (All)",
    "providers.audience.all.hint": "Inclus pour chaque travailleur envoyé.",
    "providers.audience.individual.label": "Particuliers (employés, p. ex. W2)",
    "providers.audience.individual.hint":
      "Remplace \"Tout le monde\" pour les travailleurs payés à titre de personne.",
    "providers.audience.organization.label": "Organisations (incorporées / C2C)",
    "providers.audience.organization.hint":
      "Remplace \"Tout le monde\" pour les travailleurs payés par l'intermédiaire de leur propre organisation.",
    "providers.triggers.title": "Quand les travailleurs sont envoyés automatiquement",
    "providers.triggers.onApproval": "Lorsque leur profil est approuvé",
    "providers.triggers.onChange": "Lorsque leurs détails changent (à venir)",
    "providers.triggers.onTermination": "Lorsque leur mandat prend fin (à venir)",
    "providers.triggers.reconcile":
      "Rattraper les travailleurs oubliés (réconciliation — vérification toutes les quelques heures)",
    "providers.triggers.hint":
      "\"Rattraper les travailleurs oubliés\" envoie automatiquement tout travailleur actif d'un type coché qui n'est pas encore dans l'onglet Travailleurs. Laissez l'option désactivée jusqu'à ce que vous ayez ajouté vos travailleurs existants dans l'onglet \"Ajouter les travailleurs existants\" — sinon, elle enverra d'elle-même tous les travailleurs historiques.",

    // Credentials
    "credentials.title": "Identifiants de connexion pour {provider} (API OAuth credentials)",
    "credentials.hint":
      "Le compte que {provider} a fourni à votre entreprise pour cette connexion — pas votre propre identifiant FBO. Les valeurs enregistrées sont chiffrées et ne sont plus jamais affichées; les champs ci-dessous restent vides intentionnellement. Saisissez une valeur uniquement pour la définir ou la remplacer.",
    "credentials.lastChanged": "Dernière modification le {date}.",
    "credentials.lastChangedBy": "Dernière modification le {date} par {name}.",
    "credentials.environmentLabel": "Ces identifiants concernent (environnement du fournisseur)",
    "credentials.clientId": "ID client",
    "credentials.clientSecret": "Secret client",
    "credentials.placeholderSaved": "•••••••• (enregistré)",
    "credentials.placeholderUnset": "Pas encore défini",
    "credentials.save": "Enregistrer les identifiants",
    "credentials.toast.nothingEntered":
      "Saisissez d'abord une nouvelle valeur dans au moins un champ",
    "credentials.toast.saved": "Identifiants de connexion enregistrés",
    "credentials.toast.savedDescription": "Ils sont stockés chiffrés et ne peuvent pas être relus.",
    "credentials.toast.saveFailed": "Les identifiants de connexion n'ont pas pu être enregistrés",
  },
}
